import styled from "styled-components";

const font = {
  family: `'Inter', system-ui, sans-serif`
};

const colors = {
  ink: "#2c2420",
  teal: "#3d6f73",
  tealDeep: "#2a4f54",
  sand: "#f3e6d4",
  cream: "#fff8f1",
  gold: "#e0b070",
  clay: "#c45c3e",
  white: "#ffffff",
  muted: "#6f6460"
};

export const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 480px;
  margin: auto;
  min-height: 100vh;
  background-color: ${colors.cream};
  position: relative;
  font-family: ${font.family};
  color: ${colors.ink};

  h1,
  h2,
  h3,
  p,
  label,
  input,
  select,
  button,
  a {
    font-family: ${font.family};
  }

  h1,
  h2,
  h3 {
    color: ${colors.ink};
  }

  h1 {
    font-size: 34px;
    line-height: 40px;
    font-weight: 700;
    margin: 0;
  }

  h2 {
    font-size: 24px;
    line-height: 30px;
    font-weight: 700;
    margin: 0;
  }

  h3 {
    font-size: 16px;
    line-height: 22px;
    font-weight: 700;
    margin: 0;
  }

  p {
    font-size: 15px;
    line-height: 23px;
    font-weight: 400;
    margin: 0;
    color: ${colors.ink};
  }

  .bold {
    font-weight: 700;
  }

  .muted {
    color: ${colors.muted};
  }

  a {
    color: inherit;
    text-decoration: underline;
    cursor: pointer;
  }

  ::selection {
    background: ${colors.teal};
    color: ${colors.cream};
  }

  caret-color: ${colors.ink};
  scrollbar-color: ${colors.teal} ${colors.sand};

  *::-webkit-scrollbar {
    width: 8px;
  }

  *::-webkit-scrollbar-thumb {
    background: ${colors.teal};
    border-radius: 8px;
  }

  .copy-button {
    appearance: none;
    border: none;
    background: transparent;
    padding: 0;
    min-width: 44px;
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border-radius: 8px;
    flex-shrink: 0;

    img {
      width: 18px;
      height: 18px;
    }

    &:focus-visible {
      outline: 2px solid ${colors.gold};
      outline-offset: 2px;
    }
  }
`;

export const NavBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: ${colors.tealDeep};

  .wordmark {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .brand {
    color: ${colors.sand};
    font-size: 13px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    font-weight: 700;
  }

  .event {
    color: ${colors.gold};
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
  }
`;

export const Hero = styled.section`
  position: relative;
  min-height: 280px;
  background: ${colors.tealDeep};

  img {
    width: 100%;
    height: 280px;
    aspect-ratio: 3 / 2;
    object-fit: cover;
    display: block;
  }

  .overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(42, 79, 84, 0.12) 0%,
      rgba(42, 79, 84, 0.86) 100%
    );
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding: 24px 20px;
    gap: 12px;
  }

  h1 {
    color: ${colors.white};
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: ${({ $tone }) =>
    $tone === "gold" ? colors.gold : "rgba(255, 255, 255, 0.16)"};
  color: ${({ $tone }) => ($tone === "gold" ? colors.ink : colors.white)};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

export const Section = styled.section`
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .section-head {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .eyebrow {
    color: ${colors.clay};
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  &.teal {
    background: ${colors.teal};
    color: ${colors.white};

    > h1,
    > h2,
    > h3,
    > p,
    .section-head h2,
    .section-head p {
      color: ${colors.white};
    }

    > .eyebrow,
    .section-head .eyebrow {
      color: ${colors.gold};
    }

    > .muted {
      color: rgba(255, 248, 241, 0.86);
    }
  }

  &.sand {
    background: ${colors.sand};
  }

  .muted {
    color: ${colors.muted};
  }
`;

export const TileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

export const Tile = styled.button`
  border: none;
  padding: 0;
  text-align: left;
  background: ${colors.white};
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(16, 34, 31, 0.08);
  transition: transform 160ms cubic-bezier(0.22, 1, 0.36, 1);

  img {
    width: 100%;
    height: 92px;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    display: block;
  }

  .tile-copy {
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  p {
    font-size: 13px;
    line-height: 18px;
    color: ${colors.muted};
  }

  &:hover {
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid ${colors.tealDeep};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

export const ScheduleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const DayCard = styled.article`
  background: ${colors.white};
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: ${colors.ink};

  h1,
  h2,
  h3,
  p {
    color: ${colors.ink};
  }

  .muted {
    color: ${colors.muted};
  }

  .day-date {
    color: ${colors.teal};
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .agenda-item {
    display: grid;
    grid-template-columns: 92px 1fr;
    gap: 10px;
    padding-top: 8px;
    border-top: 1px solid rgba(61, 111, 115, 0.16);
  }

  .time {
    color: ${colors.clay};
    font-weight: 700;
    font-size: 13px;
    line-height: 20px;
  }
`;

export const HotelCardWrap = styled.article`
  overflow: hidden;
  border-radius: 16px;
  background: ${colors.white};
  box-shadow: 0 8px 24px rgba(16, 34, 31, 0.08);

  img {
    width: 100%;
    height: 160px;
    object-fit: cover;
    display: block;
  }

  .hotel-body {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  h3 {
    color: ${colors.ink};
  }

  address {
    font-style: normal;
    color: ${colors.muted};
    font-size: 15px;
    line-height: 23px;
    white-space: pre-line;
  }
`;

export const ActionButton = styled.button`
  border: none;
  border-radius: 10px;
  min-height: 42px;
  padding: 10px 16px;
  background: ${({ $variant }) =>
    $variant === "ghost" ? "transparent" : colors.clay};
  color: ${({ $variant }) =>
    $variant === "ghost" ? colors.clay : colors.white};
  border: 1px solid ${colors.clay};
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  box-sizing: border-box;
  line-height: 1;

  && {
    color: ${({ $variant }) =>
      $variant === "ghost" ? colors.clay : colors.white};
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid ${colors.tealDeep};
    outline-offset: 2px;
  }

  &:hover:not(:disabled) {
    background: ${({ $variant }) =>
      $variant === "ghost"
        ? colors.sand
        : `color-mix(in srgb, ${colors.clay} 86%, ${colors.ink})`};
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`;

export const Footer = styled.footer`
  margin-top: auto;
  padding: 24px 20px;
  background: ${colors.tealDeep};
  color: ${colors.sand};
  display: flex;
  flex-direction: column;
  gap: 12px;

  h1,
  h2,
  h3,
  a,
  p {
    color: ${colors.sand};
    text-decoration: none;
    font-size: 13px;
  }

  .copy-row {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .legal {
    opacity: 0.78;
  }

  .version {
    opacity: 0.7;
  }
`;

export const MissingParams = styled.div`
  padding: 48px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 60vh;
  justify-content: center;

  code {
    background: ${colors.sand};
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 13px;
  }
`;

export const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  height: 100vh;
  background: rgba(42, 79, 84, 0.72);
  z-index: 40;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 16px 12px 24px;
`;

export const OverlayPanel = styled.div`
  position: relative;
  width: 100%;
  max-height: calc(100vh - 40px);
  background: ${colors.cream};
  border-radius: 18px;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .overlay-hero {
    position: relative;
    height: 128px;
    background: ${colors.tealDeep};

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    h2 {
      position: absolute;
      left: 16px;
      bottom: 14px;
      color: ${colors.white};
    }
  }

  .close-button {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 50%;
    background: rgba(42, 79, 84, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 2;

    img {
      width: 12px;
      height: 12px;
    }

    &:focus-visible {
      outline: 2px solid ${colors.white};
      outline-offset: 2px;
    }
  }

  .scroll-view {
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    padding-bottom: 16px;
  }

  .section {
    padding: 16px 20px 8px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .error-message {
    color: #b42318;
  }

  .claim-form {
    padding-bottom: 24px;

    label {
      font-size: 13px;
      font-weight: 700;
    }

    input,
    select {
      width: 100%;
      min-height: 42px;
      border: 1px solid ${colors.teal};
      border-radius: 8px;
      padding: 10px 12px;
      font-size: 15px;
      background: ${colors.white};
      color: ${colors.ink};

      &:focus-visible {
        outline: 2px solid ${colors.tealDeep};
        outline-offset: 2px;
      }
    }
  }

  .confirm-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(61, 111, 115, 0.16);
  }

  .balance-figure {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 16px 0 8px;
    text-align: center;
  }

  .balance-value {
    font-size: 32px;
    line-height: 38px;
    font-weight: 700;
    color: ${colors.teal};
    font-variant-numeric: tabular-nums;
  }

  .activation-code {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 12px 14px;
    border-radius: 10px;
    border: 1px solid ${colors.teal};
    background: ${colors.white};

    p {
      font-size: 22px;
      font-weight: 700;
      letter-spacing: 0.04em;
      font-variant-numeric: tabular-nums;
    }
  }

`;

export const PassCard = styled.div`
  margin: 8px 18px 24px;
  border-radius: 18px;
  background: linear-gradient(160deg, ${colors.teal} 0%, ${colors.tealDeep} 100%);
  color: ${colors.white};
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 220px;

  h1,
  h2,
  h3,
  p {
    color: ${colors.white};
  }

  .pass-brand {
    font-size: 11px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: ${colors.gold};
    font-weight: 700;
  }

  .pass-meta {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: auto;
  }

  .pass-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
    font-variant-numeric: tabular-nums;

    span {
      color: rgba(255, 248, 241, 0.72);
    }
  }
`;

export const PageStatus = styled.div`
  min-height: 50vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px 24px;
  text-align: center;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid ${colors.sand};
    border-top-color: ${colors.teal};
    border-radius: 50%;
    animation: demo-event-spin 0.8s linear infinite;
  }

  @keyframes demo-event-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .spinner {
      animation: none;
      opacity: 0.7;
    }
  }
`;
